/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable camelcase */
import { GRADE_CODE_TO_PLP_MAP } from "@/ts/constants";
import {
    AcademicTranscriptPreviewInterface,
    Assignment,
    AssignmentResultEntry,
    ElectiveCourse,
    ElectivePerformance,
    ElectiveSubject,
    IntegratedLearningAreaInterface,
    Lesson,
    QuizResultInterface,
    SubjectPerformance,
    SubjectResulInterface
} from "@/ts/interfaces/Reporting/AcademicTranscriptPreviewInterface";
import { ArchiveInterface } from "@/ts/interfaces/Reporting/ArchiveDetailsInterface";
import { AssignmentByTermInterface } from "@/ts/interfaces/Reporting/AssignmentDetailsByTermInterface";
import { LessonByTermInterface } from "@/ts/interfaces/Reporting/CourseDetailByTermInterface";
import { CourseLessonDataInterface } from "@/ts/interfaces/Reporting/CourseLessonDataInterface";
import {
    GradeSubjectFeedbackDetailsInterface,
    GradeSubjectFeedbackMappingInterface,
    GradeSubjectFeedbackOptionInterface
} from "@/ts/interfaces/Reporting/GradeSubjectFeedbackInterface";
import {
    LessonActivityInterface,
    LessonGroupByWeek
} from "@/ts/interfaces/Reporting/LessonActivityInterface";
import { QuizDetailsWithActivitiesInterface } from "@/ts/interfaces/Reporting/QuizDetailsInterface";
import {
    MappedQuestion,
    MappedQuizResult,
    ProcessedOption,
    QuizResultsApiResponse
} from "@/ts/interfaces/Reporting/QuizResultsInterface";
import {
    LessonInterface,
    WeekInterface
} from "@/ts/interfaces/Reporting/ReportingInterface";
import { StudentEntitlementInterface } from "@/ts/interfaces/Reporting/StudentEntitlementInterface";
import {
    FeedbackMapping,
    FeedbackOption,
    StudentFeedbackDetails,
    WorkSampleLessonActivityInterface,
    WorkSampleUploadInterface
} from "@/ts/interfaces/Reporting/StudentFeedbackInterface";
import {
    AdditionalSubjectInterface,
    ElectiveCourseSubjectInterface,
    StudentSubjectsInterface
} from "@/ts/interfaces/Reporting/StudentSubjectsInterface";
import {
    SubjectFeedbackForGradeDetailsInterface,
    SubjectFeedbackForGradeMappingInterface,
    SubjectFeedbackForGradeOptionInterface,
    SubjectFeedbackForGradeTermAnswersInterface
} from "@/ts/interfaces/Reporting/SubjectFeedbackForGradeInterface";
import {
    AllFeedbackDetailsApiInterface,
    AllFeedbackDetailsInterface
} from "@/ts/interfaces/Reporting/SubjectFeedbackInterface";
import {
    AcademicTranscriptPreviewApiInterface,
    AssignmentApiInterface,
    AssignmentResultEntryApiInterface,
    ElectiveApi,
    ElectiveSubjectApi,
    IntegratedLearningAreaApiInterface,
    LessonApiInterface,
    QuizResultApiInterface,
    SubjectPerformanceApiInterface,
    SubjectResultApiInterface
} from "@/ts/interfaces/Reporting/api/AcademicTranscriptPreviewApiInterface";
import { ArchiveApiInterface } from "@/ts/interfaces/Reporting/api/ArchiveDetailsApiInterface";
import { AssignmentDetailApiInterface } from "@/ts/interfaces/Reporting/api/AssignmentDetailsByTermApiInterface";
import { CourseDetailApiInterface } from "@/ts/interfaces/Reporting/api/CourseDetailByTermApiInterface";
import { CourseLessonDataApiInterface } from "@/ts/interfaces/Reporting/api/CourseLessonDataApiInterface";
import {
    GradeSubjectAnswerMappingApiInterface,
    GradeSubjectFeedbackApiInterface,
    GradeSubjectFeedbackOptionApiInterface
} from "@/ts/interfaces/Reporting/api/GradeSubjectFeedbackApiInterface";
import { LessonActivityApiInterface } from "@/ts/interfaces/Reporting/api/LessonActivityApiInterface";
import {
    LessonCurriculumOutcome,
    LessonCurriculumOutcomeApi
} from "@/ts/interfaces/Reporting/api/LessonCurriculamInterface";
import { QuizDetailsWithActivitiesApiInterface } from "@/ts/interfaces/Reporting/api/QuizDetailsApiInterface";
import { StudentEntitlementsApiInterface } from "@/ts/interfaces/Reporting/api/StudentEntitlementsApiInterface";
import {
    ReportingFeedbackMapping as ReportingFeedbackMappingApi,
    ReportingFeedbackOption as ReportingFeedbackOptionApi,
    StudentFeedbackDetailsResponse,
    WorkSampleLessonActivityApiInterface,
    WorkSampleUploadApiInterface
} from "@/ts/interfaces/Reporting/api/StudentFeedbackApiDetailInterface";
import { StudentSubjectsApiInterface } from "@/ts/interfaces/Reporting/api/StudentSubjectsApiInterface";
import {
    SubjectFeedbackForGradeApiInterface,
    SubjectFeedbackForGradeOptionApiInterface,
    SubjectFeedbackForGradeTermAnswersApiInterface,
    SubjectFeedbackForGradetAnswerMappingApiInterface
} from "@/ts/interfaces/Reporting/api/SubjectFeedbackForGradeApiInterface";

import { mapStatus } from "./markingPortalDataMapper";
import { parseMetadata } from "./reportingHelper";
import { getStudentLevel } from "./utils";

export interface LessonActivityWithWeek extends LessonActivityInterface {
    id: number;
    week: string;
}

export const mapGradeLessonDetailsByTermWithApiData = (
    apiData: CourseDetailApiInterface[]
): LessonByTermInterface[] => {
    const subjects: LessonByTermInterface[] = [];

    apiData.forEach(course => {
        course.terms.forEach(term => {
            term.subjects.forEach(subject => {
                let completedLessons = 0;
                let totalLessons = 0;

                const weekInfo: WeekInterface[] = subject.weeks.map(week => {
                    const lessons: LessonInterface[] = week.lessons.map(
                        lesson => {
                            totalLessons++;
                            if (lesson.completed_at !== null) {
                                completedLessons++;
                            }

                            // Transform API lesson to LessonInterface
                            return {
                                activityId: lesson.activity_id,
                                lessonId: lesson.lesson_id,
                                lessonName: lesson.lesson_name,
                                startedAt: lesson.started_at,
                                completedAt: lesson.completed_at,
                                updatedAt: lesson.updated_at
                            };
                        }
                    );

                    return {
                        week: week.week,
                        lessons
                    };
                });

                subjects.push({
                    gradeCode: course.grade,
                    courseId: subject.course_id,
                    subjectId: subject.subject_id,
                    gradeSubjectId: subject.grade_subject_id,
                    term: term.term,
                    name: subject.subject_name,
                    noOfLessonsCompleted: completedLessons,
                    totalLessons,
                    weeks: weekInfo
                });
            });
        });
    });

    return subjects;
};

export const mapQuizDetailsWithActivitiesApiData = (
    apiData: QuizDetailsWithActivitiesApiInterface[]
): QuizDetailsWithActivitiesInterface[] => {
    const quizzes: QuizDetailsWithActivitiesInterface[] = [];
    apiData.forEach(quiz => {
        quizzes.push({
            grade: quiz.grade,
            gradeCode: quiz.grade_code,
            term: quiz.term,
            subject: quiz.subject,
            subjectId: quiz.subject_id,
            activityId: quiz.activity_id,
            score: quiz.score,
            maxScore: quiz.max_score,
            attemptCount: quiz.attempt_count
        });
    });

    return quizzes;
};

export const mapCourseLessonDataWithApiData = (
    apiData: CourseLessonDataApiInterface[]
): CourseLessonDataInterface[] => {
    return apiData.map(course => ({
        courseId: course.course_id,
        subjectId: course.subject_id,
        term: course.term,
        productFamilyGradeId: course.product_family_grade_id,
        gradeCode: course.grade_code,
        lessonCount: course.lesson_count
    }));
};

export const mapGradeAssignmentMarksByTermWithApiData = (
    apiData: AssignmentDetailApiInterface[]
): AssignmentByTermInterface[] => {
    return apiData?.map(assignment => ({
        term: assignment.term,
        subjects: (assignment.subjects || []).map(subject => ({
            name: subject.name,
            subjectId: subject.subject_id,
            gradeSubjectId: subject.grade_subject_id,
            assignmentPercentage: subject.assignment_percentage,
            assignmentUUID: subject.assignment_uuid,
            status: mapStatus(subject.status)
        }))
    }));
};

export const mapStudentEntitlementsApiData = (
    apiData: StudentEntitlementsApiInterface[]
): StudentEntitlementInterface[] => {
    return apiData?.map(student => ({
        student: {
            eukaId: student.euka_id,
            studentId: student.student_id,
            studentName: student.student_name,
            studentFirstName: student.student_name?.split(" ")[0] || "",
            avatarKey: student.avatar_key
        },
        grades: [...(student.grades || [])]
            .sort((a, b) => {
                if (a.grade_code === 901) return -1; // Preparatory first
                if (b.grade_code === 901) return 1; // Preparatory first
                return a.grade_code - b.grade_code;
            })
            .map(grade => ({
                productFamilyGradeId: grade.product_family_grade_id,
                productFamilyName: grade.product_family_name,
                level: getStudentLevel(grade.product_family_name),
                gradeCode: grade.grade_code,
                shortName: grade.short_name,
                longName: grade.long_name,
                licenseId: grade.license_id,
                courses: (grade.courses || []).map(course => ({
                    id: course.id,
                    name: course.name
                }))
            }))
    }));
};

export const mapStudentSubjectsApiData = (
    apiData: StudentSubjectsApiInterface,
    gradeCode: string
): StudentSubjectsInterface => {
    const terms = apiData?.terms?.map(term => ({
        term: term.term,
        notIncluded: term.not_included,
        subjects: term.subjects
            .sort((a, b) => a.subject_name.localeCompare(b.subject_name))
            .map(subject => ({
                id: subject.subject_id,
                name: subject.subject_name,
                type: subject.subject_type,
                lessonCount: subject.lesson_count,
                gradeSubjectId: subject.grade_subject_id,
                gradeCode: subject.grade_code,
                plp:
                    gradeCode !== subject.grade_code.toString()
                        ? GRADE_CODE_TO_PLP_MAP[subject.grade_code]
                        : null,
                allWeeks: subject.weeks,
                personalLearningPlanId: subject.personal_learning_plan_id,
                courseId: subject.course_id,
                feedbackStatus: subject.feedback_status,
                gradeFeedbackStatus: subject.grade_feedback_status
            }))
    }));

    const courseWithElectivesArr: ElectiveCourseSubjectInterface[] =
        apiData?.electives
            ?.filter(subject => !!subject?.subject_id)
            ?.map(subject => ({
                subjectId: subject.subject_id,
                subjectName: subject.subject_name,
                courses:
                    subject.courses &&
                    subject.courses
                        .sort((a, b) =>
                            a.course_name.localeCompare(b.course_name)
                        )
                        .map(course => ({
                            id: course.course_id,
                            name: course.course_name,
                            lessonCount: course.lesson_count,
                            gradeCode: course.grade_code,
                            plp:
                                gradeCode !== course.grade_code.toString()
                                    ? GRADE_CODE_TO_PLP_MAP[course.grade_code]
                                    : null,
                            allWeeks: course.weeks,
                            feedbackStatus: course.feedback_status,
                            gradeFeedbackStatus: course.grade_feedback_status
                        }))
            })) ?? [];
    const additionalSubjects: AdditionalSubjectInterface[] = (
        apiData.additional_subjects ?? []
    )
        .sort((a, b) => a.subject_name.localeCompare(b.subject_name))
        .map(subject => ({
            subjectId: subject.subject_id,
            courseId: subject.course_id,
            name: subject.subject_name,
            type: subject.subject_type,
            lessonCount: subject.lesson_count,
            gradeSubjectId: subject.grade_subject_id,
            gradeCode: subject.grade_code,
            plp:
                subject.personal_learning_plan_id !== null
                    ? GRADE_CODE_TO_PLP_MAP[subject.grade_code]
                    : null,
            allWeeks: subject.weeks,
            feedbackStatus: subject.feedback_status,
            gradeFeedbackStatus: subject.grade_feedback_status
        }));

    const hasUniversityPathway = apiData.has_university_pathway;

    return {
        terms,
        electives: courseWithElectivesArr,
        additionalSubjects,
        hasUniversityPathway
    };
};

export const mapLessonActivitiesByWeek = (
    apiDataByWeek: Record<string, LessonActivityApiInterface[]>
): LessonGroupByWeek[] => {
    return Object.entries(apiDataByWeek).map(([weekKey, items]) => {
        const weekNum = Number(weekKey);
        const lessons: LessonActivityInterface[] = items.map(item => ({
            lessonId: item.lesson_id,
            activityId: item.activity_id,
            lessonName: item.lesson_name,
            lessonOrder: item.lesson_order,
            startedAt: item.started_at,
            completedAt: item.completed_at,
            lesson: null,
            updatedAt: item.updated_at
        }));

        return {
            weekKey: weekNum,
            weekLabel: `Week ${weekKey}`,
            lessons
        };
    });
};

export const mapStudentFeedbackDetailsApiData = (
    apiData: StudentFeedbackDetailsResponse["data"]
): StudentFeedbackDetails => {
    const { answers } = apiData;

    const parsedMetadata = parseMetadata(answers?.metadata);

    return {
        options: apiData?.options?.map(
            (opt: ReportingFeedbackOptionApi): FeedbackOption => ({
                id: opt.id,
                type: opt.key,
                optionText: opt.option_text,
                icon: opt.icon,
                order: opt.order,
                createdAt: opt.created_at,
                updatedAt: opt.updated_at
            })
        ),

        answers: apiData?.answers
            ? {
                  id: apiData.answers.id,
                  userId: apiData.answers.user_id,
                  termId: apiData.answers.term_id,
                  subjectId: apiData.answers.subject_id,
                  productFamilyGradeId: apiData.answers.product_family_grade_id,
                  comment: parsedMetadata.comment,
                  createdAt: apiData.answers.created_at,
                  updatedAt: apiData.answers.updated_at,
                  mappings: (apiData.answers.mappings || []).map(
                      (m: ReportingFeedbackMappingApi): FeedbackMapping => ({
                          id: m.id,
                          reportingFeedbackId: m.reporting_feedback_id,
                          reportingFeedbackOptionId:
                              m.reporting_feedback_option_id,
                          type: m.type,
                          createdAt: m.created_at,
                          updatedAt: m.updated_at
                      })
                  )
              }
            : {
                  id: 0,
                  userId: 0,
                  termId: 0,
                  subjectId: null,
                  productFamilyGradeId: 0,
                  comment: "",
                  createdAt: "",
                  updatedAt: "",
                  mappings: []
              },
        workSample: apiData?.work_sample
            ? mapWorkSampleLessonActivity(apiData?.work_sample)
            : null,
        isOverallTermFeedbackStatus: apiData?.overall_term_feedback_status,
        achievementStandardFull: apiData?.achievement_standard_full,
        achievementStandardSummary: apiData?.achievement_standard_summary
    };
};

export const mapWorkSampleLessonActivity = (
    api: WorkSampleLessonActivityApiInterface
): WorkSampleLessonActivityInterface => ({
    uuid: api.uuid,
    studentId: api.student_id,
    productFamilyGradeId: api.product_family_grade_id,
    term: api.term,
    lessonId: api.lesson?.id,
    lesson: api.lesson?.lesson,
    week: api.lesson?.week,
    gradeCode: api.lesson?.grade_code,
    personalLearningPlanId: api.lesson?.personal_learning_plan_id,
    weeks: api.weeks
        ? api.weeks?.map(weekInfo => ({
              week: weekInfo.week_id,
              lessonCount: weekInfo.lesson_count,
              completedByWeek: 0,
              lessons: weekInfo.lessons.map(l => ({
                  activityId: l.lesson_id,
                  lessonId: l.lesson_id,
                  lessonName: l.title,
                  startedAt: Date.now(),
                  completedAt: null,
                  updatedAt: Date.now()
              }))
          }))
        : [],
    feedback: api.feedback,
    comment: api.comment,
    type: api.type,
    subjectType: api.subject_type,
    inactivatedAt: api.inactivated_at,
    subject:
        api.subject !== null
            ? {
                  id: api.subject.id,
                  name: api.subject.name
              }
            : null,
    course:
        api?.course !== null
            ? {
                  id: api?.course.id,
                  name: api?.course.name
              }
            : null,
    workSampleUploads: api.work_sample_uploads?.map(upload => ({
        uuid: upload.uuid,
        assetId: upload.asset_id,
        fileName: upload.file_name,
        fileDownloadUrl: upload.file_download_url,
        status: upload.status,
        disk: upload.disk
    }))
});

export const mapGradeSubjectFeedbackDetailsApiData = (
    apiData: GradeSubjectFeedbackApiInterface
): GradeSubjectFeedbackDetailsInterface => {
    const { answers } = apiData;

    const parsedMetadata = parseMetadata(answers?.metadata);

    return {
        options: apiData?.options?.map(
            (
                opt: GradeSubjectFeedbackOptionApiInterface
            ): GradeSubjectFeedbackOptionInterface => ({
                id: opt.id,
                type: opt.type,
                optionText: opt.option_text,
                icon: opt.icon,
                key: opt.key,
                order: opt.order,
                createdAt: opt.created_at,
                updatedAt: opt.updated_at,
                optionUuid: opt.option_uuid,
                isPremium: opt.is_premium,
                isSubject: opt.is_subject
            })
        ),

        answers: apiData?.answers
            ? {
                  id: apiData.answers.id,
                  userId: apiData.answers.user_id,
                  subjectId: apiData.answers.subject_id,
                  productFamilyGradeId: apiData.answers.product_family_grade_id,
                  createdAt: apiData.answers.created_at,
                  updatedAt: apiData.answers.updated_at,
                  metadata: parsedMetadata,
                  mappings: (apiData.answers.mappings || []).map(
                      (
                          m: GradeSubjectAnswerMappingApiInterface
                      ): GradeSubjectFeedbackMappingInterface => ({
                          id: m.id,
                          reportingFeedbackId: m.reporting_feedback_id,
                          reportingFeedbackOptionId:
                              m.reporting_feedback_option_id,
                          type: m.type,
                          createdAt: m.created_at,
                          updatedAt: m.updated_at
                      })
                  )
              }
            : {
                  id: 0,
                  userId: 0,
                  subjectId: null,
                  productFamilyGradeId: 0,
                  createdAt: "",
                  updatedAt: "",
                  metadata: {},
                  mappings: []
              }
    };
};

export const mapQuizResultsApiData = (
    apiData: QuizResultsApiResponse
): MappedQuizResult => {
    const questions: MappedQuestion[] = apiData.responses.map(response => {
        const {
            question_number,
            score,
            attempted_option,
            processed_options,
            question
        } = response;
        const { stimulus, validation } = question.data;

        const formatLabel = (opt: ProcessedOption): string => {
            return `${opt.choice_label || ""} ) ${opt.label}`;
        };

        const options = processed_options.map(opt => ({
            label: formatLabel(opt),
            value: opt.value
        }));

        const selected = processed_options.find(
            opt => opt.value === attempted_option.value
        );
        const correct = processed_options.find(opt =>
            validation.valid_response.value.includes(opt.value)
        );

        return {
            questionHeading: `Question ${question_number}`,
            questionStimulus: stimulus,
            isCorrect: score === 1,
            selectedLabel: selected ? formatLabel(selected) : "Not Answered",
            correctLabel: correct ? formatLabel(correct) : "Not Available",
            options
        };
    });

    return {
        score: apiData.score,
        maxScore: apiData.max_score,
        status: apiData.status,
        activityId: apiData.activity_id,
        questions
    };
};

export const mapAcademicTranscriptPreviewApiData = (
    apiData: AcademicTranscriptPreviewApiInterface
): AcademicTranscriptPreviewInterface => {
    const sortedElectives = apiData.electives
        ? [...apiData.electives].sort((a, b) => a.name.localeCompare(b.name))
        : [];

    const mappedElectives = sortedElectives.map(
        mapElectiveApiToSubjectPerformanceApi
    );

    return {
        grade: apiData.grade,
        studentName: apiData.student_name,
        studentFirstName: apiData.student_first_name,
        date: apiData.date,
        term: apiData.term,
        hasUniversityPathway: apiData.has_university_pathway,
        integratedLearningArea: mapIntegratedLearningArea(
            apiData.integrated_learning_area
        ),
        isSeniorGrade: apiData.is_senior_grade,
        subjects: apiData.subjects.map(
            mapSubjectPerformanceApiInterfaceToSubjectPerformance
        ),
        electiveSubjects: mapElectiveSubjects(apiData.elective_subjects),
        electives: mappedElectives,
        favouriteSubject: apiData.favourite_subject,
        favouriteSubjectWhy: apiData.favourite_subject_why,
        homeschoolingExperience: {
            homeschoolExperienceWhy:
                apiData?.homeschooling_experience?.home_school_experience_why,
            icon: apiData?.homeschooling_experience?.icon,
            learningStyle: apiData?.homeschooling_experience?.learning_style,
            learningStyleValues:
                apiData?.homeschooling_experience?.learning_style_values,
            text: apiData?.homeschooling_experience?.text,
            wereChangesMade:
                apiData?.homeschooling_experience?.were_changes_made,
            wereGoalsAchieved:
                apiData?.homeschooling_experience?.were_goals_achieved
        },
        lowLights: apiData?.low_lights,
        highLights: apiData?.high_lights,
        achievementhighlightsInterface: apiData.highlights,
        workHabits: apiData.work_habits,
        workEthic: apiData.work_ethic,
        attitude: apiData.attitude,
        comment: apiData.comment,
        summary: apiData.summary,
        workSampleErrors: Array.isArray(apiData.work_sample_errors)
            ? apiData.work_sample_errors.map(mapWorkSampleLessonActivity)
            : []
    };
};

const mapSubjectPerformanceApiInterfaceToSubjectPerformance = (
    subject: SubjectPerformanceApiInterface
): SubjectPerformance => {
    console.log(subject, "subject");

    const performanceData = getSubjectResultByType(
        subject.result,
        "PERFORMANCE"
    );
    const workEthicData = getSubjectResultByType(subject.result, "WORK ETHIC");
    const lessonData = getSubjectResultByType(subject.result, "LESSONS");
    const completedLessonCount =
        lessonData !== null ? lessonData.completeLessonCount : null;
    const totalLessonCount =
        lessonData !== null ? lessonData.lessonCount : null;

    const assignmentPercentage =
        subject.assignments.length > 0
            ? getMarkResultFromAssignment(subject.assignments[0].result)
            : null;

    const quizzData = mapQuizResult(subject.quiz);

    return {
        id: subject.subject_id,
        courseId: subject.course_id,
        isAdditionalSubject: subject.is_additional_subject,
        name: subject.name,
        gradeCode: subject.grade_code,
        columns: subject.columns,
        comment: subject.comment,
        isPLP: subject.is_plp_subject,
        gradeFeedbackStatus: subject.grade_feedback_status,
        subjectOverviewLabel: subject.subject_overview_label,
        personalLearningPlanId: subject.personal_learning_plan_id,
        performance: performanceData !== null ? performanceData.grade : null,
        workEthic: workEthicData !== null ? workEthicData.grade : null,
        performanceIcon: performanceData !== null ? performanceData.icon : null,
        subjectExistTerms: subject.subject_exist_terms,
        workEthicIcon: workEthicData !== null ? workEthicData.icon : null,
        completedLessonCount,
        totalLessonCount,
        quizzes: quizzData,
        assignmentPercentage:
            assignmentPercentage !== null
                ? Number(assignmentPercentage?.marks ?? 0)
                : null,
        achievementStandard: subject.achievement_standard,
        learningStyle: subject.learning_style,
        result: subject.result.map(mapSubjectResult),
        lessons: subject.lessons.map(mapLesson),
        assignments: subject.assignments.map(mapAssignment)
    };
};

export const mapElectiveApiToSubjectPerformanceApi = (
    elective: ElectiveApi
): ElectivePerformance => {
    const result = elective.result ?? [];

    const performanceData = getSubjectResultByType(result, "PERFORMANCE");
    const workEthicData = getSubjectResultByType(result, "WORK ETHIC");
    const lessonData = getSubjectResultByType(result, "LESSONS");
    const completedLessonCount =
        lessonData !== null ? lessonData.completeLessonCount : null;
    const totalLessonCount =
        lessonData !== null ? lessonData.lessonCount : null;
    // some of the data fall back to defults
    return {
        id: elective.subject_id,
        courseId: elective.course_id,
        name: elective.name,
        gradeCode: elective.grade_id,
        columns: 1,
        isPLP: elective.is_plp_subject ?? false,
        gradeFeedbackStatus: false,
        personalLearningPlanId: elective.personal_learning_plan_id,
        comment: elective.comment,
        achievementStandard: elective.achievement_standard,
        learningStyle: elective.learning_style,
        subjectOverviewLabel: elective.subject_overview_label,
        quizzes: null,
        lessons: elective.lessons ?? [],
        assignments: [],
        result: result.map(mapSubjectResult),
        performance: performanceData !== null ? performanceData.grade : null,
        workEthic: workEthicData !== null ? workEthicData.grade : null,
        performanceIcon: performanceData !== null ? performanceData.icon : null,
        workEthicIcon: workEthicData !== null ? workEthicData.icon : null,
        completedLessonCount,
        totalLessonCount,
        assignmentPercentage: null
    };
};

const mapSubjectResult = (
    result: SubjectResultApiInterface
): SubjectResulInterface => {
    return {
        type: result.type,
        grade: result.grade,
        marks: result.marks,
        icon: result.icon,
        status: result.status,
        completeLessonCount:
            result.complete_lesson_count !== undefined
                ? result.complete_lesson_count
                : null,
        lessonCount:
            result.lesson_count !== undefined ? result.lesson_count : null
    };
};

const mapQuizResult = (
    result: QuizResultApiInterface | null | undefined
): QuizResultInterface | null => {
    if (!result) {
        return null;
    }

    return {
        marks: result.marks,
        questionsCount: result.questions_count,
        correctCount: result.correct_count,
        attempts: result.attempts,
        questions: result?.questions?.map(question => ({
            question: question?.question,
            questionNumber: question?.question_number,
            givenAnswers: question?.given_answers,
            correctAnswers: question?.correct_answers,
            isCorrect: question?.is_correct,
            itemReference: question?.item_reference,
            reference: question?.reference
        }))
    };
};

const mapLesson = (lesson: LessonApiInterface): Lesson => {
    return {
        subjectName: lesson.subject_name,
        title: lesson.title,
        week: lesson.week,
        curriculum: lesson.curriculum.map(curriculum => ({
            code: curriculum.code,
            description: curriculum.description
        })),
        thumbnail: [...lesson.thumbnail],
        full: [...lesson.full],
        comment: lesson.comment,
        feedback: lesson.feedback
    };
};

const mapAssignment = (assignment: AssignmentApiInterface): Assignment => {
    return {
        name: assignment.name,
        columns: assignment.columns,
        learningOutcome: Array.isArray(assignment.learning_outcome)
            ? [...assignment.learning_outcome]
            : assignment.learning_outcome
              ? [assignment.learning_outcome]
              : [],
        feedback: assignment.feedback,
        brief: assignment.brief,
        purpose: assignment.purpose,
        result: assignment.result.map(mapAssignmentResult)
    };
};

const mapAssignmentResult = (
    result: AssignmentResultEntryApiInterface
): AssignmentResultEntry => {
    return {
        type: result.type,
        grade: result.grade,
        marks: parseFloat(result.marks),
        icon: result.icon,
        markValue: result.mark_value !== undefined ? result.mark_value : null,
        maxMark: result.max_mark !== undefined ? result.max_mark : null
    };
};

export const getSubjectResultByType = (
    results: SubjectResultApiInterface[],
    type: string
): SubjectResulInterface | null => {
    const result = results.find(result => result.type === type);

    if (!result) return null;

    return {
        type: result.type,
        grade: result.grade,
        marks: result.marks,
        icon: result.icon,
        status: result.status,
        completeLessonCount:
            result.complete_lesson_count !== undefined
                ? result.complete_lesson_count
                : null,
        lessonCount:
            result.lesson_count !== undefined ? result.lesson_count : null
    };
};

export const getMarkResultFromAssignment = (
    results: AssignmentResultEntryApiInterface[]
): AssignmentResultEntry | null => {
    const markEntry = results.find(result => result.type === "MARK");

    if (!markEntry) {
        return null;
    }

    if (!markEntry.marks) {
        return null;
    }

    return {
        type: markEntry.type,
        grade: markEntry.grade,
        marks: parseFloat(markEntry.marks.replace("%", "")),
        icon: markEntry.icon,
        markValue: markEntry.mark_value ?? null,
        maxMark: markEntry.max_mark ?? null
    };
};

export const mapAllFeedbackDetailsApiData = (
    apiData: AllFeedbackDetailsApiInterface
): AllFeedbackDetailsInterface & { isAllFeedbacksCompleted: boolean } => {
    const { answers } = apiData.data.term_feedback;

    const parsedMetadata = parseMetadata(answers?.metadata);

    const termFeedbackAnswer = apiData.data.term_feedback.answers
        ? {
              id: apiData.data.term_feedback.answers.id,
              userId: apiData.data.term_feedback.answers.user_id,
              termId: apiData.data.term_feedback.answers.term_id,
              subjectId: apiData.data.term_feedback.answers.subject_id,
              productFamilyGradeId:
                  apiData.data.term_feedback.answers.product_family_grade_id,
              comment: parsedMetadata.comment,
              createdAt: apiData.data.term_feedback.answers.created_at,
              updatedAt: apiData.data.term_feedback.answers.updated_at,
              mappings: (apiData.data.term_feedback.answers.mappings || []).map(
                  mapping => ({
                      id: mapping.id,
                      reportingFeedbackId: mapping.reporting_feedback_id,
                      reportingFeedbackOptionId:
                          mapping.reporting_feedback_option_id,
                      type: mapping.type,
                      createdAt: mapping.created_at,
                      updatedAt: mapping.updated_at
                  })
              )
          }
        : null;

    const subjectFeedbackSubjects = (
        apiData.data.subject_feedback.subjects || []
    ).map(subject => {
        let parsedMetadata: Record<string, any> = {};

        // Safely parse the metadata (string or object)
        if (subject.answers?.metadata) {
            if (typeof subject.answers.metadata === "string") {
                try {
                    parsedMetadata = JSON.parse(subject.answers.metadata);
                } catch (error) {
                    console.error(
                        "Failed to parse subject metadata JSON:",
                        error
                    );
                }
            } else {
                parsedMetadata = subject.answers.metadata || {};
            }
        }

        return {
            subjectId: subject.subject_id,
            courseId: null,
            subjectName: subject.subject_name,
            isPlp: subject.is_plp,
            feedbackStatus: subject.feedback_status,
            plpGradeCode: subject.grade_code,
            personalLearningPlanId: subject.personal_learning_plan_id,
            plpText:
                subject.grade_code && subject.is_plp
                    ? GRADE_CODE_TO_PLP_MAP[subject.grade_code]
                    : undefined,
            answers: subject.answers
                ? {
                      id: subject.answers.id,
                      userId: subject.answers.user_id,
                      termId: subject.answers.term_id,
                      subjectId: subject.answers.subject_id,
                      productFamilyGradeId:
                          subject.answers.product_family_grade_id,
                      comment: parsedMetadata.comment,
                      createdAt: subject.answers.created_at,
                      updatedAt: subject.answers.updated_at,
                      mappings: (subject.answers.mappings || []).map(
                          mapping => ({
                              id: mapping.id,
                              reportingFeedbackId:
                                  mapping.reporting_feedback_id,
                              reportingFeedbackOptionId:
                                  mapping.reporting_feedback_option_id,
                              type: mapping.type,
                              createdAt: mapping.created_at,
                              updatedAt: mapping.updated_at
                          })
                      )
                  }
                : null
        };
    });

    const isAllFeedbacksCompleted =
        !!termFeedbackAnswer &&
        subjectFeedbackSubjects.every(
            subject => subject.feedbackStatus === true
        );

    return {
        termFeedback: {
            options: (apiData.data.term_feedback.options || []).map(option => ({
                id: option.id,
                type: option.type,
                optionText: option.option_text,
                icon: option.icon,
                order: option.order,
                isSubject: option.is_subject === 1
            })),
            answers: termFeedbackAnswer
        },
        subjectFeedback: {
            options: (apiData.data.subject_feedback.options || []).map(
                option => ({
                    id: option.id,
                    type: option.type,
                    optionText: option.option_text,
                    icon: option.icon,
                    order: option.order,
                    isSubject: option.is_subject === 1
                })
            ),
            subjects: subjectFeedbackSubjects
        },
        message: apiData.message,
        isAllFeedbacksCompleted
    };
};

export const mapPayloadDataForFeedbackSubmission = (data?: any): any => {
    return {
        term_id: data?.term_id,
        subject_id: data?.subject_id,
        course_id: data?.course_id,
        product_family_grade_id: data?.product_family_grade_id,
        personal_learning_plan_id: data?.personal_learning_plan_id,
        metadata: {
            comment: data?.comment
        },
        answers: data?.answers.map(
            (a: { type: string; option_id: number }) => ({
                key: a.type,
                option_id: a.option_id
            })
        ),
        type: data?.type
    };
};

export const mapSubjectFeedbackForGradeDetailsApiData = (
    apiData: SubjectFeedbackForGradeApiInterface
): SubjectFeedbackForGradeDetailsInterface => {
    let parsedMetadata: Record<string, any> = {};

    const { answers } = apiData;

    if (answers?.metadata) {
        if (typeof answers.metadata === "string") {
            try {
                parsedMetadata = JSON.parse(answers.metadata);
            } catch (error) {
                console.error("Failed to parse metadata JSON:", error);
            }
        } else {
            parsedMetadata = answers.metadata || {};
        }
    } else {
        parsedMetadata = {};
    }

    return {
        options: apiData?.options?.map(
            (
                opt: SubjectFeedbackForGradeOptionApiInterface
            ): SubjectFeedbackForGradeOptionInterface => ({
                id: opt.id,
                type: opt.type,
                optionText: opt.option_text,
                icon: opt.icon,
                key: opt.key,
                order: opt.order,
                createdAt: opt.created_at,
                updatedAt: opt.updated_at,
                optionUuid: opt.option_uuid,
                isPremium: opt.is_premium,
                isSubject: opt.is_subject
            })
        ),

        answers: apiData?.answers
            ? {
                  id: apiData.answers.id,
                  userId: apiData.answers.user_id,
                  subjectId: apiData.answers.subject_id,
                  productFamilyGradeId: apiData.answers.product_family_grade_id,
                  createdAt: apiData.answers.created_at,
                  updatedAt: apiData.answers.updated_at,
                  metadata: parsedMetadata,
                  mappings: (apiData.answers.mappings || []).map(
                      (
                          m: GradeSubjectAnswerMappingApiInterface
                      ): GradeSubjectFeedbackMappingInterface => ({
                          id: m.id,
                          reportingFeedbackId: m.reporting_feedback_id,
                          reportingFeedbackOptionId:
                              m.reporting_feedback_option_id,
                          type: m.type,
                          createdAt: m.created_at,
                          updatedAt: m.updated_at
                      })
                  )
              }
            : {
                  id: 0,
                  userId: 0,
                  subjectId: null,
                  productFamilyGradeId: 0,
                  createdAt: "",
                  updatedAt: "",
                  metadata: {},
                  mappings: []
              },
        termAnswers:
            apiData?.termAnswers?.map(
                (
                    termAnswer: SubjectFeedbackForGradeTermAnswersApiInterface
                ): SubjectFeedbackForGradeTermAnswersInterface => ({
                    id: termAnswer.id,
                    user_id: termAnswer.user_id,
                    term_id: termAnswer.term_id,
                    subject_id: termAnswer.subject_id,
                    course_id: termAnswer.course_id,
                    personal_learning_plan_id:
                        termAnswer.personal_learning_plan_id,
                    mappings: (termAnswer.mappings || []).map(
                        (
                            mapping: SubjectFeedbackForGradetAnswerMappingApiInterface
                        ): SubjectFeedbackForGradeMappingInterface => ({
                            id: mapping.id,
                            reportingFeedbackId: mapping.reporting_feedback_id,
                            reportingFeedbackOptionId:
                                mapping.reporting_feedback_option_id,
                            type: mapping.type,
                            createdAt: mapping.created_at,
                            updatedAt: mapping.updated_at
                        })
                    )
                })
            ) || [],
        achievementStandardFull: apiData?.achievement_standard_full,
        achievementStandardSummary: apiData?.achievement_standard_summary
    };
};

export const mapArchiveApiData = (
    apiData: ArchiveApiInterface[] | null | undefined
): ArchiveInterface[] => {
    if (!apiData || apiData.length === 0) return [];

    return apiData.map(item => ({
        studentId: item.student_id,
        firstName: item.student_first_name,
        lastName: item.student_last_name,
        docs: (item.download_ || []).map(doc => ({
            fileName: doc.file_name,
            signedUrl: doc.signed_url
        }))
    }));
};

export const mapWorkSampleUpload = (
    item: WorkSampleUploadApiInterface
): WorkSampleUploadInterface => ({
    uuid: item.uuid,
    assetId: item.asset_id,
    fileName: item.file_name,
    fileDownloadUrl: item.file_download_url,
    status: item.status,
    disk: item.disk
});

export const mapWorkSampleUploadArray = (
    apiList: WorkSampleUploadApiInterface[]
): WorkSampleUploadInterface[] => {
    if (!apiList) return [];

    return apiList
        .map(mapWorkSampleUpload)
        .filter((item): item is WorkSampleUploadInterface => item !== null);
};

export const mapElectiveSubjects = (
    electiveSubjects: ElectiveSubjectApi[]
): ElectiveSubject[] => {
    if (!Array.isArray(electiveSubjects)) return [];

    return electiveSubjects.map(subj => {
        const courses: ElectiveCourse[] =
            subj.courses?.map(c => ({
                id: c.course_id,
                wpCourseId: c.wp_course_id,
                openEdxCourseId: c.open_edx_course_id,
                subjectId: c.subject_id,
                name: c.course_name,
                gradeSubjectId: c.grade_subject_id,
                grade: c.grade,
                term: c.term,
                countryId: c.country_id,
                status: c.status,
                isCore: c.is_core,
                entitlementId: c.entitlement_id,
                subject: c.subject,
                weekLabelOverride: c.week_label_override,
                createdAt: c.created_at,
                updatedAt: c.updated_at,
                deletedAt: c.deleted_at,
                feedbackStatus: c.feedback_status,
                gradeFeedbackStatus: c.grade_feedback_status
            })) ?? [];

        // Compute main.subject gradeFeedbackStatus
        const gradeFeedbackStatus =
            courses.length > 0 &&
            courses.every(x => x.gradeFeedbackStatus === true);

        return {
            subjectId: subj.subject_id,
            subjectName: subj.subject_name,
            weeks: subj.weeks,
            lessonCount: subj.lesson_count,
            courses,
            gradeFeedbackStatus
        };
    });
};

export const mapIntegratedLearningArea = (
    integratedLearningAreas: IntegratedLearningAreaApiInterface[]
): IntegratedLearningAreaInterface[] => {
    if (!Array.isArray(integratedLearningAreas)) return [];

    return integratedLearningAreas.map(area => ({
        name: area.name,
        deliveryModel: area.delivery_model,
        achievemenStandard: area.achievement_standard
    }));
};

export const mapLessonDetailsCurriculumOutcomes = (
    outcomes: LessonCurriculumOutcomeApi[] | undefined
): LessonCurriculumOutcome[] => {
    if (!Array.isArray(outcomes)) return [];

    return outcomes.map(outcome => ({
        id: outcome.id,
        code: outcome.code,
        contentDescription: outcome.content_description,
        topic: outcome.topic ?? null,
        lessonElaboration: outcome.lesson_elaboration ?? null,
        subStrand: outcome.sub_strand ?? null,
        version: outcome.version
    }));
};
